import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import {
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    CircularProgress,
    Box,
    Alert,
    Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person"; // Default avatar icon
import { useAppDispatch, useAppSelector } from "../../store/hook"; // Use typed hooks
import { RootState } from "../../store/store";
import { fetchConversations, setActiveConversation } from "../../store/slices/messagingSlice";
import { fetchProfile, selectUserProfileById } from "../../store/slices/profileSlice"; // Import fetchProfile and selector
import { markNotificationRead, Notification } from "../../store/slices/notificationsSlice";
import TimeAgo from "timeago-react"; // <-- Import TimeAgo
// import PropTypes from "prop-types"; // <-- Keep PropTypes commented out/removed

// Helper function to safely parse timestamp
const parseTimestamp = (timestamp: number | undefined): Date | number => {
    if (timestamp === undefined || timestamp === null) {
        // Return current time as a fallback if timestamp is missing
        return Date.now();
    }
    try {
        const date = new Date(timestamp);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            console.warn(`[ConversationsList] Invalid timestamp received: ${timestamp}`);
            // Return current time as a fallback
            return Date.now();
        }
        return date;
    } catch (error) {
        console.error(`[ConversationsList] Error parsing timestamp: ${timestamp}`, error);
        // Return current time as a fallback
        return Date.now();
    }
};

// Define props for ConversationsList
interface ConversationsListProps {
    unreadConversationIds: string[];
}

const ConversationsList: React.FC<ConversationsListProps> = ({ unreadConversationIds }) => {
    // Destructure props
    const dispatch = useAppDispatch();

    // Select necessary data from the Redux store
    const {
        items: conversations,
        loading,
        error,
    } = useAppSelector((state: RootState) => state.messaging.conversations);
    const activeConversationId = useAppSelector(
        (state: RootState) => state.messaging.activeConversationId,
    );
    const currentUser = useAppSelector((state: RootState) => state.auth.user); // Get current user info

    // Select the entire profiles state slice to check for existing profiles
    const profilesState = useAppSelector((state: RootState) => state.profile);

    // Select notifications to find relevant ones to mark as read
    const notifications = useAppSelector((state: RootState) => state.notifications.notifications);

    // Fetch conversations when the component mounts or needs refresh
    useEffect(() => {
        if (loading === "idle") {
            dispatch(fetchConversations());
        }
    }, [dispatch, loading]);

    // --- NEW Effect: Fetch profiles for participants ---
    useEffect(() => {
        // Only run this after conversations have successfully loaded
        if (loading === "succeeded" && conversations.length > 0) {
            console.log(
                "[ConversationsList] Conversations loaded, checking participant profiles...",
            );
            // Create a set of unique participant IDs that are not the current user
            const participantIdsToFetch = new Set<string>();
            conversations.forEach((convo) => {
                convo.participants.forEach((participantId) => {
                    if (participantId !== currentUser?.id) {
                        // Check if profile for this ID is already loading or loaded in profileSlice state
                        const profileEntry = profilesState?.profiles?.[participantId];
                        const profileStatus = profileEntry?.loading;
                        const profileDataExists = !!profileEntry?.data;

                        // Only add to fetch set if not current user, and profile is not already loading or present
                        if (profileStatus !== "pending" && !profileDataExists) {
                            participantIdsToFetch.add(participantId);
                            console.log(
                                `[ConversationsList] Will fetch profile for: ${participantId}`,
                            );
                        }
                    }
                });
            });

            // Dispatch fetchProfile for each unique ID identified
            participantIdsToFetch.forEach((id) => {
                console.log(`[ConversationsList] Dispatching fetchProfile for: ${id}`);
                // Note: This might dispatch multiple fetch requests concurrently.
                // Consider batching or more sophisticated caching if performance becomes an issue.
                dispatch(fetchProfile(id));
            });
        }
        // Depend on the conversations array reference and loading status
        // When fetchConversations succeeds, loading goes to 'succeeded' and conversations array reference changes, triggering this.
    }, [conversations, loading, currentUser?.id, dispatch, profilesState]);
    // ----------------------------------------------

    // Handler for clicking on a conversation item
    const handleConversationClick = (conversationId: string) => {
        console.log(`[ConversationsList] Clicked conversation: ${conversationId}`);

        // Check if this conversation was marked as unread
        if (unreadConversationIds.includes(conversationId)) {
            console.log(
                `[ConversationsList] Conversation ${conversationId} was unread. Marking associated notifications as read.`,
            );
            // Find unread 'message' notifications related to this conversation (entityId)
            const notificationsToMark = notifications.filter(
                (n: Notification) =>
                    n.entityId === conversationId && n.type === "message" && n.status === "unread",
            );

            // Dispatch markRead for each relevant notification
            notificationsToMark.forEach((notification: Notification) => {
                console.log(
                    `[ConversationsList] Dispatching markNotificationRead for: ${notification.notificationId}`,
                );
                dispatch(markNotificationRead(notification.notificationId));
            });
        }

        // Set the conversation active (this should happen regardless)
        console.log(`[ConversationsList] Setting active conversation: ${conversationId}`);
        dispatch(setActiveConversation(conversationId));
    };

    // Helper component to render each conversation item (fetches profile internally)
    // Define props for ConversationItem
    interface ConversationItemProps {
        conversation: (typeof conversations)[0];
        isUnread: boolean; // Add isUnread prop
    }
    const ConversationItem: React.FC<ConversationItemProps> = React.memo(
        ({ conversation, isUnread }) => {
            // Destructure isUnread
            const otherParticipantId = conversation.participants.find((p) => p !== currentUser?.id);

            // Use the selector to get profile data for the other participant
            // This selector should handle returning null/undefined if the profile isn't loaded yet
            const participantProfile = useAppSelector((state) =>
                otherParticipantId ? selectUserProfileById(state, otherParticipantId) : null,
            );

            // Determine display name based on fetched profile data
            const displayName = participantProfile?.firstName
                ? `${participantProfile.firstName} ${participantProfile.lastName || ""}`.trim()
                : otherParticipantId
                  ? `User ${otherParticipantId.substring(0, 6)}...` // Fallback to ID
                  : "Unknown User";

            const avatarUrl = participantProfile?.photoUrl || undefined; // Use profile photo if available

            return (
                <ListItem disablePadding>
                    <ListItemButton
                        selected={conversation.id === activeConversationId}
                        onClick={() => handleConversationClick(conversation.id)}
                        sx={{
                            // Apply primary color background if unread
                            backgroundColor: isUnread ? "var(--primary-color)" : undefined,
                            "&.Mui-selected": {
                                // Make selected style override unread if needed, or adjust logic
                                backgroundColor: "action.selected",
                                "&:hover": { backgroundColor: "action.hover" },
                            },
                            // Optionally add hover effect for unread items
                            "&:hover": {
                                backgroundColor: isUnread
                                    ? "var(--primary-light-color)"
                                    : undefined, // Lighter primary on hover
                            },
                        }}
                    >
                        <ListItemAvatar>
                            <Avatar src={avatarUrl}>
                                {!avatarUrl && <PersonIcon />} {/* Show icon if no photoUrl */}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="body1" noWrap sx={{ fontWeight: 500 }}>
                                    {displayName}
                                </Typography>
                            }
                            secondary={
                                <Typography
                                    variant="body2"
                                    noWrap
                                    color="text.secondary"
                                    component="span"
                                >
                                    {conversation.lastMessageSnippet
                                        ? `${conversation.lastMessageSnippet.slice(0, 30)}${conversation.lastMessageSnippet.length > 30 ? "..." : ""}`
                                        : "No messages yet"}
                                </Typography>
                            }
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1, whiteSpace: "nowrap" }}
                        >
                            <TimeAgo
                                datetime={parseTimestamp(conversation.updatedAt)}
                                locale="en_US"
                            />
                        </Typography>
                    </ListItemButton>
                </ListItem>
            );
        },
    );

    // Remove PropTypes definition again
    // ConversationItem.propTypes = {
    //     conversation: PropTypes.object.isRequired, // Use generic object type
    // };
    ConversationItem.displayName = "ConversationItem"; // Keep displayName

    // --- Render Logic ---

    // 1. Show Loading Indicator
    if (loading === "pending") {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    p: 2,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // 2. Show Error Message
    if (loading === "failed") {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">Error loading conversations: {error}</Alert>
            </Box>
        );
    }

    // 3. Show "No Conversations" Message
    if (loading === "succeeded" && conversations.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body1">No conversations yet.</Typography>
                <Typography variant="body2">Start a new chat!</Typography>
                {/* TODO: Add a button/link here to initiate a new conversation */}
            </Box>
        );
    }

    // 4. Render the List of Conversations
    return (
        <List disablePadding>
            {conversations.map((convo, index) => {
                // Determine if the current conversation is unread
                const isUnread = unreadConversationIds.includes(convo.id);
                // Use the memoized item component
                return (
                    <React.Fragment key={convo.id}>
                        <ConversationItem conversation={convo} isUnread={isUnread} />{" "}
                        {/* Pass isUnread */}
                        {index < conversations.length - 1 && (
                            <Divider variant="inset" component="li" />
                        )}
                    </React.Fragment>
                );
            })}
        </List>
    );
};

export default ConversationsList;
